/* eslint-disable import/prefer-default-export */
import { FieldFunctionOptions, FieldPolicy, Reference } from '@apollo/client'

import {
  Filter,
  FilterMap,
  getCacheKey,
  getFilteredData,
  getFilters,
  getNewIncomingData,
  getPaginatedData,
} from '../utils'

import { FilterDealsOwnersInput, DealOwnerObj } from '~generated/types'

type IncomingDealsOwners = {
  docs: Reference[]
  totalPages: number
  totalDocs: number
  page: number
}

type ExistingDealsOwners = {
  [key: string]: IncomingDealsOwners
}

export class PaginatedDealsOwnersQueryCache implements FieldPolicy {
  keyArgs: false

  merge = (
    existing: ExistingDealsOwners,
    incoming: IncomingDealsOwners,
    { readField, canRead, variables }: FieldFunctionOptions
  ) => {
    const cacheKey = getCacheKey({ ...variables?.filter, ...variables?.sort })
    const { docs: existingDocs = [], ...restExisting } = existing?.[cacheKey] || {}
    const { docs: incomingDocs = [], ...restIncoming } = incoming

    const newIncomingDocs = getNewIncomingData({ incomingData: incomingDocs, existingData: existingDocs, readField })
    const newExistingDocs = [...existingDocs.filter(canRead), ...newIncomingDocs]

    return {
      ...existing,
      [cacheKey]: {
        ...restExisting,
        ...restIncoming,
        docs: newExistingDocs,
      },
    }
  }

  read = (existing: ExistingDealsOwners, options: FieldFunctionOptions) => {
    if (!existing) return undefined

    const { variables, readField } = options
    const { pagination, filter, sort } = variables

    const filters = getFilters(filter, this.filters)
    const cacheKey = getCacheKey({ ...filter, ...sort })
    const cachedData = existing[cacheKey]

    if (!cachedData) return undefined

    const filteredDocs = getFilteredData({
      data: cachedData.docs,
      filter,
      filters,
      readField,
    })

    const paginatedDocs = getPaginatedData({
      filteredData: filteredDocs,
      page: cachedData?.page,
      limit: pagination?.limit,
    })

    return { ...cachedData, docs: paginatedDocs }
  }

  /**
   * Filters
   */

  get filters() {
    return {
      query: this.query,
    } as FilterMap<FilterDealsOwnersInput>
  }

  query: Filter<FilterDealsOwnersInput> = (ref, filter, readField) => {
    const nameKey: keyof DealOwnerObj = 'name'
    const name = readField(nameKey, ref) as string
    const searchRegExp = new RegExp(filter.search, 'i')
    return searchRegExp.test(name)
  }
}
